import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  Stack,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import SecurityIcon from "@mui/icons-material/Security";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useTheme, alpha } from "@mui/material/styles";
import { useTenant } from "../contexts/TenantContext";
import {
  fetchRegistrationsForUser,
  fetchClassById,
  fetchCompletedCourses,
  fetchSessionsForCourse,
  downloadCertificatePdf,
} from "../api/backend";

// ─── Design tokens ─────────────────────────────────────────────────────────
// Neutral/structural scale from the Beacon design system (tokens/colors.css,
// tokens/spacing.css, tokens/shadows.css). Brand colors (primary/secondary/
// warning) stay tenant-driven via theme.palette — see tPrimary/tSecondary/
// tAccent below, not here.
const FONT_DISPLAY = "'Space Grotesk', ui-sans-serif, system-ui, sans-serif";
const FONT_UI = "'Rubik', -apple-system, system-ui, 'Segoe UI', Helvetica, Arial, sans-serif";
const STATUS_ORDER = { live: 0, active: 1, upcoming: 2, completed: 3 };

const DS = {
  live: "#C2410C",
  liveLight: "var(--color-warn-bg)",
  liveBorder: "#FED7AA",
  grey: "#79628C",
  greyLight: "var(--color-surface-press-light)",
  greyBorder: "#CFCFDB",
  pageBg: "#FFFFFF",
  white: "#FFFFFF",
  text: "#1F1633",
  textSecondary: "#79628C",
  border: "#E5E7EB",
  cardBg: "#FFFFFF",
  night: "#1F1633",
  nightMuted: "rgba(255,255,255,0.72)",
  nightFaint: "rgba(255,255,255,0.18)",
  highlight: "#C2EF4E",
  highlightText: "#1F1633",
  violet: "#6A5FC1",
  danger: "#B42318",
  dangerBg: "rgba(180,35,24,0.08)",
  success: "#15803D",
  successBg: "rgba(21,128,61,0.10)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.18)",
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getCategoryMeta(category = "", tPrimary = "var(--color-primary)") {
  const c = category.toLowerCase();
  if (c.includes("fire") || c.includes("wildland"))
    return { Icon: LocalFireDepartmentIcon, bg: "var(--color-danger-bg)", color: "var(--color-danger)" };
  if (c.includes("emt") || c.includes("ems") || c.includes("medic") || c.includes("cpr") || c.includes("aha"))
    return { Icon: MedicalServicesIcon, bg: "var(--color-primary-10)", color: tPrimary };
  if (c.includes("police") || c.includes("law") || c.includes("tactical"))
    return { Icon: LocalPoliceIcon, bg: "#EDE9FE", color: "#4C1D95" };
  if (c.includes("security") || c.includes("guard"))
    return { Icon: SecurityIcon, bg: "#E0E7FF", color: "#3730A3" };
  if (c.includes("lifeguard") || c.includes("swim") || c.includes("aqua"))
    return { Icon: DirectionsBoatIcon, bg: "#CFFAFE", color: "var(--color-accent-violet)" };
  if (c.includes("hazmat") || c.includes("haz"))
    return { Icon: WarningAmberIcon, bg: "var(--color-warn-bg)", color: "var(--color-warn)" };
  if (c.includes("rescue") || c.includes("search"))
    return { Icon: WarningAmberIcon, bg: "#ECFDF5", color: "#15803D" };
  return { Icon: SchoolIcon, bg: "var(--color-surface-2)", color: tPrimary };
}

function getSessionStatus(session) {
  if (session.enabled) return "live";
  const d = session.sessionDate || session.startTime;
  if (!d) return "upcoming";
  return new Date(d) < new Date() ? "attended" : "upcoming";
}

function getCourseStatus(cls, sessions = []) {
  const liveSess = sessions.find((s) => s.enabled);
  if (liveSess) return "live";
  const now = new Date();
  const hasFuture = sessions.some((s) => {
    const d = s.sessionDate || s.startTime;
    return d && new Date(d) >= now;
  });
  if (hasFuture) return "active";
  if (!cls) return "upcoming";
  const firstDate = cls.date || (Array.isArray(cls.schedule) && cls.schedule[0]?.start);
  if (!firstDate) return "upcoming";
  return new Date(firstDate) >= now ? "upcoming" : "completed";
}

function getNextSession(sessions) {
  const now = new Date();
  const live = sessions.find((s) => s.enabled);
  if (live) return { session: live, type: "live" };
  const future = sessions
    .filter((s) => {
      const d = s.sessionDate || s.startTime;
      return d && new Date(d) >= now;
    })
    .sort((a, b) => {
      const da = new Date(a.sessionDate || a.startTime);
      const db = new Date(b.sessionDate || b.startTime);
      return da - db;
    });
  if (future.length > 0) return { session: future[0], type: "next" };
  return null;
}

function formatShortDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTime(dateStr) {
  if (!dateStr) return null;
  // Backend sends bare LocalTime strings ("09:00:00") for session start/end —
  // `new Date("09:00:00")` is Invalid Date, so anchor it to a real date first.
  const isBareTime = /^\d{1,2}:\d{2}(:\d{2})?$/.test(dateStr);
  const d = isBareTime ? new Date(`1970-01-01T${dateStr}`) : new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatCountdown(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  if (diff < 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `In ${days} day${days !== 1 ? "s" : ""}`;
  if (hours > 0) return `In ${hours} hour${hours !== 1 ? "s" : ""}`;
  return "Starting soon";
}

function formatTimeLeft(session) {
  if (!session?.endTime) return null;
  const dateStr = session.sessionDate || new Date().toISOString().slice(0, 10);
  const isBareTime = /^\d{1,2}:\d{2}(:\d{2})?$/.test(session.endTime);
  const end = isBareTime ? new Date(`${dateStr}T${session.endTime}`) : new Date(session.endTime);
  if (isNaN(end.getTime())) return null;
  const diffMs = end - new Date();
  if (diffMs <= 0) return null;
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

// ─── Progress Ring ──────────────────────────────────────────────────────────

function ProgressRing({ pct = 0, size = 36, strokeWidth = 3, color }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const resolvedColor = color || tPrimary;
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(pct, 100) / 100);
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={DS.greyBorder} strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={pct >= 100 ? tSecondary : resolvedColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Typography
        sx={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: size * 0.24, fontWeight: 800, color: DS.text, lineHeight: 1,
          fontFamily: "var(--font-display)",
        }}
      >
        {Math.round(Math.min(pct, 100))}%
      </Typography>
    </Box>
  );
}

// ─── Pip Track ──────────────────────────────────────────────────────────────

function PipTrack({ sessions, allAttended = false }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  if (!sessions || sessions.length === 0) return null;
  return (
    <Box sx={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
      {sessions.map((s, i) => {
        const status = allAttended ? "attended" : getSessionStatus(s);
        const bg =
          status === "attended" ? tSecondary :
          status === "live" ? DS.live :
          DS.greyBorder;
        return (
          <Box
            key={s.id || i}
            sx={{
              width: 8, height: 8, borderRadius: "2px", bgcolor: bg, flexShrink: 0,
              animation: status === "live" ? "pulse 2s infinite" : "none",
            }}
          />
        );
      })}
    </Box>
  );
}

// ─── Status Pill ────────────────────────────────────────────────────────────

function StatusPill({ type }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryLight = alpha(tSecondary, 0.08);
  const tSecondaryBorder = alpha(tSecondary, 0.25);
  const cfg = {
    live: { label: "Live Now", bg: DS.liveLight, color: DS.live, border: DS.liveBorder, pulse: true },
    active: { label: "Active", bg: tSecondaryLight, color: tSecondary, border: tSecondaryBorder, pulse: false },
    upcoming: { label: "Upcoming", bg: DS.greyLight, color: DS.grey, border: DS.greyBorder, pulse: false },
    completed: { label: "Completed", bg: "var(--color-primary-10)", color: tPrimary, border: "#BFDBFE", pulse: false },
  }[type] || { label: type, bg: DS.greyLight, color: DS.grey, border: DS.greyBorder };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: "4px", border: `1px solid ${cfg.border}`, bgcolor: cfg.bg }}>
      {cfg.pulse && (
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.color, animation: "pulse 2s infinite", flexShrink: 0 }} />
      )}
      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: cfg.color, lineHeight: 1.2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

// ─── Live Hero Strip ────────────────────────────────────────────────────────

function LiveHeroStrip({ rec, sessions, navigate }) {
  const liveSession = sessions.find((s) => s.enabled);
  const cls = rec.class;
  const sessionDate = liveSession?.sessionDate || liveSession?.startTime || cls?.date;
  const d = sessionDate ? new Date(sessionDate) : null;

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: DS.night,
        color: DS.white,
        display: "flex",
        alignItems: "center",
        gap: { xs: 2, sm: 3 },
        p: { xs: 2, sm: "22px 28px" },
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      {/* Date block */}
      {d && (
        <Box sx={{ bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", px: 2, py: 1, textAlign: "center", minWidth: 64, flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", color: DS.nightMuted }}>
            {d.toLocaleDateString("en-US", { month: "short" }).toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, lineHeight: 1.1 }}>{d.getDate()}</Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: DS.live, animation: "pulse 2s infinite" }} />
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#FDBA74" }}>
            Live now{cls?.category ? ` · ${cls.category}` : ""}{liveSession?.sessionNumber ? ` · Session ${liveSession.sessionNumber}` : ""}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 1, lineHeight: 1.3, fontFamily: "var(--font-display)" }}>
          {cls?.title}
        </Typography>
        <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
          {(liveSession?.startTime || cls?.date) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 13, color: DS.nightMuted }} />
              <Typography variant="caption" sx={{ color: DS.nightMuted }}>
                {formatTime(liveSession?.startTime || cls?.date)}
                {liveSession?.endTime && ` – ${formatTime(liveSession.endTime)}`}
                {formatTimeLeft(liveSession) && ` · ${formatTimeLeft(liveSession)}`}
              </Typography>
            </Box>
          )}
          {(liveSession?.location || cls?.location) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PlaceIcon sx={{ fontSize: 13, color: DS.nightMuted }} />
              <Typography variant="caption" sx={{ color: DS.nightMuted }}>
                {liveSession?.location || cls?.location}
              </Typography>
            </Box>
          )}
          {cls?.instructorName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 13, color: DS.nightMuted }} />
              <Typography variant="caption" sx={{ color: DS.nightMuted }}>{cls.instructorName}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* CTA */}
      <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/student-hub/${cls.id}`)}
          sx={{
            bgcolor: DS.live, color: DS.white, fontWeight: 700,
            borderRadius: "9px", px: 2.5, textTransform: "none",
            width: { xs: "100%", sm: "auto" },
            boxShadow: "0 2px 8px rgba(194,65,12,0.3)",
            "&:hover": { bgcolor: "#B03A0A" },
          }}
        >
          ▶ Join session
        </Button>
      </Box>
    </Box>
  );
}

// ─── Course Card ─────────────────────────────────────────────────────────────

function CourseCard({ rec, sessions, navigate, onCertView, onCertDownload }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const cls = rec.class;
  const catMeta = getCategoryMeta(cls?.category || "", tPrimary);
  const dateStatus = getCourseStatus(cls, sessions);
  const status = rec.status === "PASSED" ? "completed" : dateStatus;
  const nextInfo = getNextSession(sessions);

  const attendedCount = rec.status === "PASSED"
    ? sessions.length
    : sessions.filter((s) => getSessionStatus(s) === "attended").length;
  const progressPct = sessions.length > 0 ? Math.round((attendedCount / sessions.length) * 100) : 0;

  const handleCardClick = () => navigate(`/student-hub/${cls.id}`);
  const handleJoin = (e) => {
    e.stopPropagation();
    navigate(`/student-hub/${cls.id}`);
  };

  return (
    <Paper
      elevation={0}
      onClick={handleCardClick}
      sx={{
        borderRadius: "14px",
        border: status === "live" ? `1.5px solid ${DS.live}` : `1px solid ${DS.border}`,
        bgcolor: DS.cardBg,
        boxShadow: DS.shadowSm,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "box-shadow 0.2s, transform 0.15s, border-color 0.2s",
        "&:hover": {
          boxShadow: DS.shadowMd,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Card head */}
      <Box sx={{ p: 2.5 }}>
        {/* Icon + status row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.75 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "11px",
            bgcolor: catMeta.bg, color: catMeta.color,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <catMeta.Icon sx={{ fontSize: 24 }} />
          </Box>
          <StatusPill type={status} />
        </Box>

        {/* Category label */}
        {cls?.category && (
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: DS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
            {cls.category}
          </Typography>
        )}

        {/* Title */}
        <Typography
          fontWeight={800}
          color={DS.text}
          sx={{ fontSize: "1.1rem", lineHeight: 1.3, mb: 0.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontFamily: "var(--font-display)" }}
        >
          {cls?.title}
        </Typography>

        {/* Course code */}
        {cls?.courseNumber && (
          <Typography sx={{ fontSize: "0.78rem", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: DS.violet }}>
            {cls.courseNumber}
          </Typography>
        )}
      </Box>

      {/* Progress row */}
      {sessions.length > 0 && (
        <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.75, bgcolor: DS.greyLight, borderTop: `1px solid ${DS.border}`, borderBottom: `1px solid ${DS.border}` }}>
          <ProgressRing pct={progressPct} size={48} strokeWidth={4} color={status === "live" ? DS.live : tPrimary} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: DS.textSecondary, mb: 0.4 }}>
              Your progress
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: DS.text, mb: 0.6 }}>
              {attendedCount}/{sessions.length} sessions attended
            </Typography>
            <PipTrack sessions={sessions} allAttended={status === "completed"} />
          </Box>
        </Box>
      )}

      {/* Next session section */}
      <Box sx={{ px: 2.5, pt: 1.75, pb: sessions.length > 0 ? 1.75 : 2.5, flex: 1 }}>
        {status === "completed" ? (
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: tSecondary }}>
            Course completed
          </Typography>
        ) : nextInfo ? (
          <Box sx={{ bgcolor: DS.greyLight, borderRadius: "10px", px: 2, py: 1.75 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: status === "live" ? DS.live : DS.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.85 }}>
              {nextInfo.type === "live" ? "In session now" : sessions.length === 0 || attendedCount === 0 ? "First session" : "Next session"}
            </Typography>
            <Stack spacing={0.5}>
              {(nextInfo.session.sessionDate || nextInfo.session.startTime) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                  <CalendarTodayIcon sx={{ fontSize: 13, color: DS.textSecondary }} />
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: DS.text }}>
                    {formatShortDate(nextInfo.session.sessionDate || nextInfo.session.startTime)}
                    {nextInfo.session.startTime && ` · ${formatTime(nextInfo.session.startTime)}`}
                  </Typography>
                </Box>
              )}
              {nextInfo.session.location && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                  <PlaceIcon sx={{ fontSize: 13, color: DS.textSecondary }} />
                  <Typography sx={{ fontSize: "0.85rem", color: DS.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {nextInfo.session.location}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        ) : cls?.date ? (
          <Box sx={{ bgcolor: DS.greyLight, borderRadius: "10px", px: 2, py: 1.75 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: DS.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.85 }}>
              Class date
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
              <CalendarTodayIcon sx={{ fontSize: 13, color: DS.textSecondary }} />
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: DS.text }}>
                {formatShortDate(cls.date)}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* CTA strip */}
      <Box sx={{ px: 2.25, pb: 2.25, mt: "auto" }} onClick={(e) => e.stopPropagation()}>
        {status === "live" ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleJoin}
              sx={{ bgcolor: DS.live, color: DS.white, fontWeight: 700, borderRadius: "8px", textTransform: "none", "&:hover": { bgcolor: "#B03A0A" } }}
            >
              Join session
            </Button>
            <IconButton
              size="small"
              onClick={handleCardClick}
              sx={{ border: `1px solid ${DS.liveBorder}`, borderRadius: "8px", color: DS.live }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ) : status === "completed" ? (
          <Box>
            {rec.certificate && (
              <Typography data-testid="completion-certificate-info" sx={{ fontSize: "0.72rem", color: DS.textSecondary, mb: 0.75 }}>
                Certificate {rec.certificate.certNumber}
                {rec.certificate.issueDate ? ` · Completed ${rec.certificate.issueDate}` : ""}
              </Typography>
            )}
            {rec.certificate && (
              <Box sx={{ display: "flex", gap: 0.75, mb: 0.75 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onCertView(rec); }}
                  sx={{
                    flex: 1, borderColor: DS.border, color: DS.textSecondary, fontWeight: 600,
                    borderRadius: "8px", textTransform: "none", fontSize: "0.78rem",
                    "&:hover": { borderColor: tPrimary, color: tPrimary, bgcolor: alpha(tPrimary, 0.04) },
                  }}
                >
                  Record
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<WorkspacePremiumIcon sx={{ fontSize: 13 }} />}
                  onClick={(e) => { e.stopPropagation(); onCertDownload(rec); }}
                  sx={{
                    flex: 2, bgcolor: tPrimary, color: "#fff", fontWeight: 700,
                    borderRadius: "8px", textTransform: "none", fontSize: "0.78rem",
                    "&:hover": { bgcolor: tPrimaryDark },
                  }}
                >
                  Download certificate
                </Button>
              </Box>
            )}
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={handleCardClick}
              sx={{ color: DS.textSecondary, fontWeight: 600, borderRadius: "8px", textTransform: "none", fontSize: "0.78rem" }}
            >
              View archive
            </Button>
          </Box>
        ) : (
          <Box>
            {nextInfo?.session && (
              <Typography sx={{ fontSize: "0.7rem", color: DS.textSecondary, mb: 0.75, textAlign: "center" }}>
                {formatCountdown(nextInfo.session.sessionDate || nextInfo.session.startTime)}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleCardClick}
              sx={{
                borderColor: DS.border, color: DS.text, fontWeight: 600,
                borderRadius: "8px", textTransform: "none",
                "&:hover": { borderColor: tPrimary, color: tPrimary, bgcolor: alpha(tPrimary, 0.04) },
              }}
            >
              View details
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TranscriptPage() {
  const { user, isFeatureEnabled } = useTenant();
  const navigate = useNavigate();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;

  const [records, setRecords] = useState([]);
  const [sessionsMap, setSessionsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");


  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const loadData = async () => {
      const completedCourses = await fetchCompletedCourses(user.id).catch(() => []);

      const regs = await fetchRegistrationsForUser(user.id).catch(() => []);
      // Class completion certificates (certificates table): proof of completion, no expiry, not a tracked certification.
      const certs = Array.isArray(completedCourses) ? completedCourses : [];

      const result = [];
      for (const r of (Array.isArray(regs) ? regs : [])) {
        const cls = await fetchClassById(r.classId).catch(() => null);
        if (!cls) continue;
        const certificate = certs.find((c) => c.courseId === cls.id) || null;
        const hasPassed = !!certificate || r.status === "PASSED";
        const isCompleted = r.status === "COMPLETED" || r.status === "PASSED" || hasPassed;
        result.push({
          id: r.id,
          class: cls,
          certificate,
          registration: r,
          status: isCompleted ? (hasPassed ? "PASSED" : "COMPLETED") : "IN_PROGRESS",
          completedDate: certificate?.issueDate || r.completionDate || cls.date,
          type: "taken",
        });
      }
      setRecords(result);

      // Fetch sessions for all classes
      const sessionsResults = await Promise.allSettled(
        result.map((rec) => fetchSessionsForCourse(rec.class.id).then((s) => ({ id: rec.class.id, sessions: Array.isArray(s) ? s : [] })))
      );
      const map = {};
      for (const r of sessionsResults) {
        if (r.status === "fulfilled") map[r.value.id] = r.value.sessions;
      }
      setSessionsMap(map);
      setLoading(false);
    };

    loadData();
  }, [user]); // eslint-disable-line

  const liveRec = records.find((r) => {
    const sessions = sessionsMap[r.class.id] || [];
    return sessions.some((s) => s.enabled);
  });
  const liveRecSessions = liveRec ? (sessionsMap[liveRec.class.id] || []) : [];

  // ── Filters ──────────────────────────────────────────────────────────────────
  const categories = [...new Set(records.map((r) => r.class?.category).filter(Boolean))];

  const filteredRecords = useMemo(() => {
    const filtered = records.filter((rec) => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !searchQuery ||
        rec.class?.title?.toLowerCase().includes(q) ||
        rec.class?.courseNumber?.toLowerCase().includes(q);

      const matchCat = categoryFilter === "all" || rec.class?.category === categoryFilter;

      const sessions = sessionsMap[rec.class?.id] || [];
      const recStatus = getCourseStatus(rec.class, sessions);
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "live" && recStatus === "live") ||
        (statusFilter === "active" && recStatus === "active") ||
        (statusFilter === "upcoming" && recStatus === "upcoming") ||
        (statusFilter === "completed" && (recStatus === "completed" || rec.status === "PASSED"));

      return matchSearch && matchCat && matchStatus;
    });

    return filtered.sort((a, b) => {
      const sa = getCourseStatus(a.class, sessionsMap[a.class?.id] || []);
      const sb = getCourseStatus(b.class, sessionsMap[b.class?.id] || []);
      return (STATUS_ORDER[sa] ?? 99) - (STATUS_ORDER[sb] ?? 99);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, sessionsMap, searchQuery, categoryFilter, statusFilter]);

  if (!isFeatureEnabled("classes")) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>Transcript Unavailable</Typography>
        <Typography color="text.secondary">Transcript functionality requires the classes feature.</Typography>
      </Box>
    );
  }

  if (!user || (!user.roles.includes("STUDENT") && !user.roles.includes("INSTRUCTOR"))) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="error" variant="h6">You do not have permission to view this page.</Typography>
      </Box>
    );
  }

  // Live count for filter chip badge
  const liveCount = records.filter((r) => {
    const sessions = sessionsMap[r.class?.id] || [];
    return sessions.some((s) => s.enabled);
  }).length;

  // Per-status counts, mirroring the matchStatus logic above (used by filter chip badges)
  const statusCounts = { all: records.length, live: 0, active: 0, upcoming: 0, completed: 0 };
  records.forEach((rec) => {
    const sessions = sessionsMap[rec.class?.id] || [];
    const recStatus = getCourseStatus(rec.class, sessions);
    if (recStatus === "completed" || rec.status === "PASSED") statusCounts.completed += 1;
    else if (recStatus === "live") statusCounts.live += 1;
    else if (recStatus === "active") statusCounts.active += 1;
    else if (recStatus === "upcoming") statusCounts.upcoming += 1;
  });

  const sessionsThisWeekCount = Object.values(sessionsMap).flat().filter((s) => {
    const d = s.sessionDate || s.startTime;
    if (!d) return false;
    const date = new Date(d);
    const now = new Date();
    const weekAhead = new Date(Date.now() + 7 * 86400000);
    return date >= now && date <= weekAhead && !s.enabled;
  }).length;

  const handleCertView = (rec) => {
    navigate(`/my-classes/${rec.class.id}/archive`);
  };

  const handleCertDownload = async (rec) => {
    const cert = rec.certificate;
    if (!cert) return;
    try {
      const blob = await downloadCertificatePdf(cert.certificateId);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `certificate-${cert.certNumber || cert.certificateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Failed to download certificate PDF", e);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, fontFamily: FONT_UI }}>
      <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: DS.textSecondary, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
          My Training
        </Typography>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: { xs: 32, sm: 40 }, fontWeight: 700, color: DS.text, lineHeight: 1.1, mb: 1 }}>
          My Classes
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: DS.text }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          {liveCount > 0 && (
            <>
              {" · You are "}
              <Box component="span" sx={{ display: "inline-flex", px: 1, py: 0.1, borderRadius: "4px", bgcolor: DS.highlight, color: DS.highlightText, fontWeight: 700 }}>
                signed in to {liveCount} live session{liveCount !== 1 ? "s" : ""}
              </Box>
            </>
          )}
          {sessionsThisWeekCount > 0 && (
            <>
              {liveCount > 0 ? " and have " : " · You have "}
              <Box component="span" sx={{ display: "inline-flex", px: 1, py: 0.1, borderRadius: "4px", bgcolor: DS.highlight, color: DS.highlightText, fontWeight: 700 }}>
                {sessionsThisWeekCount} session{sessionsThisWeekCount !== 1 ? "s" : ""}
              </Box>
              {" coming up this week."}
            </>
          )}
        </Typography>
      </Box>

      {/* ── Live hero strip ───────────────────────────────────────────────────── */}
      {!loading && liveRec && statusFilter === "all" && (
        <LiveHeroStrip rec={liveRec} sessions={liveRecSessions} navigate={navigate} />
      )}

      {/* ── Filter toolbar ────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        {/* Segmented status filter group */}
        <Box sx={{ display: "flex", bgcolor: DS.white, border: `1px solid ${DS.border}`, borderRadius: "10px", p: "4px", gap: "2px", flexWrap: "wrap" }}>
          {[
            { key: "all", label: "All" },
            { key: "live", label: "Live" },
            { key: "active", label: "Active" },
            { key: "upcoming", label: "Upcoming" },
            { key: "completed", label: "Completed" },
          ].map(({ key, label }) => {
            const isActive = statusFilter === key;
            const accent = key === "live" ? DS.live : tPrimary;
            return (
              <Box
                key={key}
                onClick={() => setStatusFilter(key)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.5, py: 0.75, borderRadius: "8px", cursor: "pointer",
                  bgcolor: isActive ? accent : "transparent",
                  color: isActive ? DS.white : DS.text,
                  fontWeight: 600, fontSize: "0.82rem",
                  transition: "background 0.15s, color 0.15s",
                  "&:hover": { bgcolor: isActive ? accent : DS.greyLight },
                }}
              >
                {label}
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.7rem", fontWeight: 700, px: "6px", py: "1px", borderRadius: "9999px",
                    bgcolor: isActive ? "rgba(255,255,255,0.22)" : DS.greyLight,
                    color: isActive ? DS.white : DS.textSecondary,
                    border: isActive ? "none" : `1px solid ${DS.border}`,
                  }}
                >
                  {statusCounts[key]}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Search */}
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <TextField
            fullWidth
            placeholder="Search your courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: DS.textSecondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: DS.white,
                "& fieldset": { borderColor: DS.border },
                "&:hover fieldset": { borderColor: tPrimary },
                "&.Mui-focused fieldset": { borderColor: tPrimary },
              },
              "& input": { py: "7px", fontSize: "0.875rem" },
            }}
          />
        </Box>

        {/* Category select */}
        {categories.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: "10px", bgcolor: DS.white, fontSize: "0.875rem",
                "& fieldset": { borderColor: DS.border },
                "&:hover fieldset": { borderColor: tPrimary },
              }}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categories.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* ── Card grid ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />
          ))}
        </Box>
      ) : filteredRecords.length === 0 ? (
        <Paper elevation={0} sx={{ py: 8, textAlign: "center", borderRadius: "12px", border: `1px dashed ${DS.border}`, bgcolor: DS.greyLight }}>
          <SchoolIcon sx={{ fontSize: 40, color: DS.border, mb: 1.5 }} />
          <Typography variant="body1" fontWeight={600} color={DS.text} gutterBottom sx={{ fontFamily: "var(--font-display)" }}>
            No courses found
          </Typography>
          <Typography variant="body2" color={DS.textSecondary}>
            {searchQuery || statusFilter !== "all" || categoryFilter !== "all"
              ? "Try adjusting your filters."
              : "You haven't registered for any classes yet."}
          </Typography>
          {(searchQuery || statusFilter !== "all" || categoryFilter !== "all") && (
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 2, borderRadius: "8px", borderColor: DS.border, color: DS.text, textTransform: "none" }}
              onClick={() => { setSearchQuery(""); setStatusFilter("all"); setCategoryFilter("all"); }}
            >
              Clear filters
            </Button>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
          {filteredRecords.map((rec) => (
            <CourseCard
              key={rec.id}
              rec={rec}
              sessions={sessionsMap[rec.class?.id] || []}
              navigate={navigate}
              onCertView={handleCertView}
              onCertDownload={handleCertDownload}
            />
          ))}
        </Box>
      )}

    </Box>
  );
}
